import userInfo from 'utils/userInfo';

const defaultVM = {
  isPreview: false,
  isSaveToDraft: false,
  name: '',
  city: [],
  location: {
    directionLandmarks: '',
    fullAddress: '',
    latitude: 12.9770823,
    longitude: 77.5725585,
    locality: '',
    placeId: '',
  },
  summary: '',
  cuisines: [],
  mustTryDishes: [],
  alsoServes: [],
  featuredImage: null,
  eateryImages: [],
  menuImages: [],
  description: '',
  tips: [],
  tel: '',
  openingHour: [],
};

const validate = vm => {
  const errors = {};

  // Do not validate if saving as draft
  if (vm.isSaveToDraft) {
    return errors;
  }

  // List of validations when submitting the form
  if (vm.cuisines.length === 0) {
    errors.cuisines = 'Require at least one cuisine.';
  }
  if (vm.mustTryDishes.length === 0) {
    errors.mustTryDishes = 'Require at least one dish.';
  }
  if (!vm.featuredImage) {
    errors.featuredImage = 'Require featured image.';
  }
  if (vm.tips.filter(({ tip }) => tip.trim()).length === 0) {
    errors.tips = 'Require at least one tip.';
  }
  vm.openingHour.some(({ days, openingTime, closingTime }) =>
    days.length === 0
      ? (errors.openingHour = 'Require at least one day.')
      : !openingTime
      ? (errors.openingHour = 'Require opening time.')
      : !closingTime
      ? (errors.openingHour = 'Require closing time.')
      : false
  );

  if (Object.keys(errors).length) {
    errors.form = 'Please fix the errors in the form.';
  }

  return errors;
};

const mapOptions = ({ id, name: label }) => ({ value: id.toString(), label });

const mapOtherOptions = dish => ({
  value: dish,
  label: dish,
  __isNew__: true,
});

const mapImage = image =>
  image && {
    id: image.id,
    description: image.description || '',
    picCredits: image.picCredits || '',
    uri: image.uri,
  };

const openingHourToViewModel = (openingHour = []) =>
  openingHour.map(entry => {
    const [days, times] = entry.split(' ');
    const [openingTime, closingTime] = times.split('-');

    return {
      days: days.split(','),
      openingTime,
      closingTime,
    };
  });

const toViewModel = model => {
  const {
    city = {},
    location = {},
    cuisines = [],
    mustTryDishes: {
      dishes: mustTryDishes = [],
      otherDishes: mustTryOtherDishes = [],
    } = {},
    alsoServes: {
      dishes: alsoServesDishes = [],
      otherDishes: alsoServesOtherDishes = [],
    } = {},
    galleryImages = [],
    menuImages = [],
    curatorTips = [],
    contactDetails = {},
  } = model;

  location.fullAddress = location.fullAddress || '';
  location.latitude = location.latitude || 12.9770823;
  location.longitude = location.longitude || 77.5725585;

  return {
    id: model.id,
    name: model.name || '',
    city: [{ value: city.id || '', label: city.name || '' }],
    location,
    summary: model.summary || '',
    cuisines: cuisines.map(mapOptions),
    mustTryDishes: mustTryDishes
      .map(mapOptions)
      .concat(mustTryOtherDishes.map(mapOtherOptions)),
    alsoServes: alsoServesDishes
      .map(mapOptions)
      .concat(alsoServesOtherDishes.map(mapOtherOptions)),
    featuredImage: mapImage(model.mainImage),
    eateryImages: galleryImages.map(mapImage),
    menuImages: menuImages.map(mapImage),
    description: model.content || '',
    tips: curatorTips.map(({ id, tip }) => ({ id, tip })),
    // tel: contactDetails.phoneNumber || '',

    /*
     **
    If contactDetails is null then there is no point of checking phoneNumber
    **
    */
    tel: contactDetails ? contactDetails.phoneNumber || '' : '',
    openingHour: openingHourToViewModel(model.openingHour),
    // email: contactDetails.email || '',

    /*
     **
    If contactDetails is null then there is no point of checking email too
    **
    */
    email: contactDetails ? contactDetails.email || '' : '',
  };
};

const locationToModel = location => {
  const {
    directionLandmarks,
    fullAddress,
    locality,
    latitude,
    longitude,
    placeId,
  } = location;

  return {
    directionLandmarks,
    fullAddress,
    latitude,
    longitude,
    locality,
    placeId,
  };
};

const mapImageToModel = item => ({
  description: item.description || '',
  name: item.name || '', // TODO: What should 'name' be?
  picCredits: item.picCredits || '',
  uri: item.uri,
  file: item.file,
});

const reduceDishes = (acc, { value: id, __isNew__ }) => {
  if (__isNew__) {
    acc.otherDishes.push(id);
  } else {
    acc.dishes.push({ id });
  }
  return acc;
};

const openingHourToModel = openingHour =>
  openingHour.map(
    ({ days, openingTime, closingTime }) =>
      `${days.join(',')} ${openingTime}-${closingTime}`
  );

const toModel = vm => ({
  draft: vm.isSaveToDraft,
  id: vm.id,
  name: vm.name,
  city: { id: vm.city[0].value },
  location: locationToModel(vm.location),
  owner: {
    id: userInfo.getUserID(),
  },
  summary: vm.summary,
  cuisines: vm.cuisines.map(({ value: id }) => ({ id })),
  mustTryDishes: vm.mustTryDishes.reduce(reduceDishes, {
    dishes: [],
    otherDishes: [],
  }),
  alsoServes: vm.alsoServes.reduce(reduceDishes, {
    dishes: [],
    otherDishes: [],
  }),
  mainImage: mapImageToModel(vm.featuredImage),
  galleryImages: vm.eateryImages.map(mapImageToModel),
  menuImages: vm.menuImages.map(mapImageToModel),
  content: vm.description,
  contactDetails: {
    email: vm.email,
    phoneNumber: vm.tel,
  },
  openingHour: openingHourToModel(vm.openingHour),
  amenities: vm.amenities,
  ambience: Object.keys(vm.ambiences)
    .filter(id => vm.ambiences[id].value)
    .map(id => ({ id })),
});

export default {
  defaultVM,
  toModel,
  toViewModel,
  validate,
};
